import { useAtom } from 'jotai'
import { useEffect, useState } from 'react'
import { useGetUserSessionDependencies } from '~/queries/userSessionDependencyQueries'
import Logger from '~/services/Logger'
import { getAppVersion, isElectron } from '~/utils/common'
import {
  initializePosthog,
  UNKNOWN_APP_VERSION,
  UNKNOWN_CHANNEL,
} from '~/utils/initializePosthog'
import { lookupInstalledAppPartner } from '~/utils/partner'
import type { PartnerId } from '~/utils/partner'

import { ExtensionInstance, Pbx } from '@mangovoice/types'

import {
  getAuthStateFromLocalStorage,
  userStateAtom,
} from './AuthenticationState'

const logger = new Logger('UserState')

let refreshState: boolean = true

type UseUserStateProps = {
  canAutoRefresh: boolean
}

/**
 * Checks if there's a mismatch between auth state and user state.
 * Returns true if auth token exists but user state is empty.
 * Used for logging purposes to track when this condition occurs.
 */
const detectAuthUserStateMismatch = (): boolean => {
  try {
    const authState = getAuthStateFromLocalStorage()
    return Boolean(authState.appSessionJwts?.access_token)
  } catch (error) {
    logger.error('Error detecting auth/user state mismatch', null, { error })
    return false
  }
}

export const useUserState = (props?: UseUserStateProps) => {
  const [userState, setUserState] = useAtom(userStateAtom)
  const [currentChannel, setCurrentChannel] = useState<string | null>(null)
  const [appVersion, setAppVersion] = useState<string | null>(null)
  const [buildChannel, setBuildChannel] = useState<string | null>(null)
  const [electronPartnerId, setElectronPartnerId] = useState<PartnerId | null>(
    null,
  )
  const [releaseLine, setReleaseLine] = useState<string | undefined>()

  const {
    refetch: getUserSessionDependenciesInternal,
    isFetching: userStateLoading,
  } = useGetUserSessionDependencies({
    onQuerySuccess: (data) => {
      logger.info('Successfully fetched user session dependencies')

      let currentUserExtensions = userState.currentUserExtensions
      if (
        !currentUserExtensions &&
        userState.currentUserExtension &&
        userState.currentUserExtension.pbx.id === data.pbx.id
      ) {
        currentUserExtensions = {
          [data.pbx.id]: userState.currentUserExtension,
        }
      }

      setUserState({
        pbxId: data.pbx.id,
        pbx: data.pbx,
        pbxUsers: data.pbxUsers,
        sessionUser: data.sessionUser,
        pbxExtensions: data.userExtensions,
        currentUserExtensions: currentUserExtensions,
        currentUserExtensionId: currentUserExtensions?.[data.pbx.id]?.id,
        currentUserExtension: currentUserExtensions?.[data.pbx.id],
        messagingEnabled: data.pbx.features.messaging_enabled,
        enhancedCallLogsEnabled: data.pbx.features.web_app_call_logs,
        callAnalyticsEnabled: data.pbx.features.call_analytics,
        agentStatusesEnabled: data.pbx.features.agent_statuses,
        userRoles: data.userRoles,
        isErrored: false,
        isInternalUser: data.sessionUser?.email?.endsWith('@mangovoice.com'),
        botProfiles: data.botProfiles,
      })
    },
    onQueryErrored: (extensionId, error) => {
      logger.error('Failed to fetch user session dependencies', null, {
        extensionId,
        error,
      })

      const authError = error.status === 401 || error.status === 403
      if (authError) {
        return
      }

      setUserState((prev) => ({ ...prev, isErrored: true }))
    },
  })

  const setCurrentUserExtension = (
    pbxId?: number,
    extension?: ExtensionInstance,
  ) => {
    if (!pbxId) {
      return
    }
    const currentUserExtensions = userState.currentUserExtensions || {}
    if (!extension) {
      delete currentUserExtensions[pbxId]
    } else {
      currentUserExtensions[pbxId] = extension
    }
    setUserState((prev) => ({
      ...prev,
      currentUserExtensions: currentUserExtensions,
      currentUserExtensionId: extension?.id,
      currentUserExtension: extension,
    }))
  }

  const getUserSessionDependencies = (pbx: Pick<Pbx, 'id'>) => {
    getUserSessionDependenciesInternal(pbx)
  }

  useEffect(() => {
    // Resolve channel + build channel + app version so PostHog init (gated below) never stamps
    // events with a placeholder during the async startup window. All must
    // ALWAYS settle to a value — even on failure — or the gate would block
    // telemetry entirely rather than just mislabel it.
    if (isElectron) {
      const channelRequest = window.electron?.channels?.getCurrentChannel()
      if (channelRequest) {
        channelRequest
          .then((channel) => setCurrentChannel(channel))
          .catch(() => setCurrentChannel(UNKNOWN_CHANNEL))
      } else {
        setCurrentChannel(UNKNOWN_CHANNEL)
      }
      const buildChannelRequest = window.electron?.channels?.getBuildEnv()
      if (buildChannelRequest) {
        buildChannelRequest
          .then((channel) => setBuildChannel(channel))
          .catch(() => setBuildChannel(UNKNOWN_CHANNEL))
      } else {
        setBuildChannel(UNKNOWN_CHANNEL)
      }
    } else {
      setCurrentChannel('web')
      setBuildChannel('web')
    }

    getAppVersion()
      .then((version) => setAppVersion(version))
      .catch((err) => {
        logger.warn('Failed to get app version:', err)
        setAppVersion(UNKNOWN_APP_VERSION)
      })

    // PostHog's `partner` is the installed desktop build's partner only, never
    // the PBX's (that's `pbx_partner`). Settles to '' (no partner) on the web, a
    // non-partner build, or failure, so the gate below never blocks. The release
    // line is set only when the lookup succeeds, so a failure stays unknown.
    void lookupInstalledAppPartner().then((id) => {
      setReleaseLine(id === null ? undefined : id || 'mango')
      setElectronPartnerId(id ?? '')
    })
  }, [])

  // Log when auth/user-state mismatch is detected (for troubleshooting)
  useEffect(() => {
    const isEmptyUserState =
      !userState.pbx &&
      (!userState.sessionUser || Object.keys(userState).length <= 1)

    if (isEmptyUserState) {
      const hasAuthToken = detectAuthUserStateMismatch()

      if (hasAuthToken) {
        logger.warn(
          'Detected auth/user-state mismatch: auth token exists but user-session-data is empty',
          {
            userStateKeys: Object.keys(userState),
          },
        )
      }
    }
  }, [userState])

  if (refreshState && props?.canAutoRefresh) {
    if (userState.pbx) {
      void getUserSessionDependenciesInternal(userState.pbx)
      refreshState = false
    }
  }

  useEffect(() => {
    // Wait for channel + app version + partner to resolve before identifying to
    // PostHog. They start null and settle async (via electron IPC), so firing
    // early would stamp the sticky super-properties as "Unknown Channel Name" /
    // "Unknown App Version" or the wrong partner for the first events of every
    // session. All settle to a value (see the mount effect), so this never blocks.
    if (
      userState.pbx &&
      currentChannel !== null &&
      buildChannel !== null &&
      appVersion !== null &&
      electronPartnerId !== null
    ) {
      initializePosthog(
        userState.pbx,
        userState.sessionUser,
        currentChannel,
        appVersion,
        electronPartnerId,
        buildChannel,
        releaseLine,
      )
    }
  }, [
    userState.pbx,
    userState.sessionUser,
    currentChannel,
    buildChannel,
    appVersion,
    electronPartnerId,
    releaseLine,
  ])

  return {
    ...userState,
    isReady: Boolean(userState.pbx),
    isLoading: userStateLoading,
    getUserSessionDependencies,
    setCurrentUserExtension,
  }
}
